import _ from "lodash";
import React from "react";
import { AVATAR_COLORS } from "Constants/colorConstants";
import {
  getAssessmentGradesFromCache,
  getGradeBasicDetailsFromCache,
  readConfigurationFragmentV2,
} from "modules/CommonGraphqlHelpers";
import { getUserInfo } from "routes/modules/LoginModule/LoginUtils";
import {
  getMemoizedFunction,
  getMemoizedLangFunction,
  getYearDurationFormat,
  hashString,
  parseConfigurationValue,
} from "Utils";
import { store } from "store/createStore";
import {
  ArchiveOutlined,
  CopyOutlined,
  ExportOutlined,
  DeleteOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";
import {
  COURSE_BASED_GRADING,
  SUBJECT_BASED_GRADING,
} from "Constants/stringConstants";
import { getSelectedIcon } from "UIComponents/IconSelector/modules/IconSelectorUtils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  getAcademicYearsOfCurriculumProgram,
  getCurriculumProgramIdFromCurrentAdminNavigationNode,
  getAcademicYearForCurriculumProgram,
  getIsStaff,
  getUserPrimaryGradeId,
} from "modules/CommonSelectors";
import {
  LEARNING_COURSE_PERM,
  BLUEPRINT_COURSE_REVAMP,
  SHOW_COURSE_DISPLAY_NAME,
  ENABLE_DEPARTMENT_AND_LEVEL,
} from "Constants/permissionConstants";
import {
  getIsAYWisePermsSetupEnabled,
  getIsAccountConfigurationEnabled,
  getIsAccountDepartmentEnabled,
  getIsModuleSettingsV2Enabled,
} from "Utils/ACLStoreUtils";
import ACLStore from "lib/aclStore";

const getOrganizationCurriculumPrograms = state => {
  return _.get(state, "platform.organizationCurriculumPrograms", []);
};

const getSelectedCurriculumId = state => {
  return _.get(state, "platform.currentCurriculumProgram.id", undefined);
};

const getAcademicSetupTypeForCurriculum = (state, id) => {
  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );
  const curriculum = _.find(organizationCurriculumPrograms, ["id", id]);

  return _.get(curriculum, "academicSetupType", null);
};

export const parseAdminMenuId = id => {
  if (!id) return {};

  if (_.includes(id, ":")) {
    const [curriculumProgramId, nodeId] = _.split(id, ":");
    return { curriculumProgramId, nodeId };
  } else return { nodeId: id };
};

// Extract curriculumProgramId from admin portal routes
const getCurriculumProgramIdFromRoute = state => {
  let _curriculumProgramId = null;

  const loginActiveTab = _.get(state, "login.activeTab");
  const isAdminPortal = loginActiveTab === "admin";

  const isAdminNavigationRevampPortal = _.includes(
    ["accountSettingsTab", "schoolSettingsTab"],
    loginActiveTab
  );

  if (isAdminPortal) {
    const { subChildId } = _.get(state, "administrator.routes", {});
    const { curriculumProgramId } = parseAdminMenuId(subChildId);

    _curriculumProgramId = curriculumProgramId;
  }

  if (isAdminNavigationRevampPortal) {
    _curriculumProgramId = getCurriculumProgramIdFromCurrentAdminNavigationNode(
      state
    );
  }

  return _curriculumProgramId;
};

export const getAcademicSetupType = args => {
  const state = store?.getState();
  let curriculumProgramId = _.get(
    args,
    "curriculumProgramId",
    getSelectedCurriculumId(state)
  );

  if (_.isEmpty(curriculumProgramId)) {
    // try extracting curriculumProgramId from admin portal routes
    curriculumProgramId = getCurriculumProgramIdFromRoute(state);

    // can not get academicSetupType without curriculumProgramId
    if (_.isEmpty(curriculumProgramId)) return false;
  }

  const academicSetupType = getAcademicSetupTypeForCurriculum(
    state,
    curriculumProgramId
  );

  return academicSetupType;
};

export const getIsLearningCoursesEnabled = args => {
  const state = store?.getState();
  let isEnabled = false;
  const isAYWisePermsSetupEnabled = getIsAYWisePermsSetupEnabled();
  const userInfo = getUserInfo({ portalType: "PLANNER" });
  const userId = userInfo.id;
  const userType = _.get(userInfo, "user_type", "");
  const gradeId = getUserPrimaryGradeId();

  const isModuleSettingsV2Enabled = getIsModuleSettingsV2Enabled();

  let curriculumProgramId = _.get(
    args,
    "curriculumProgramId",
    _.get(state, "platform.currentCurriculumProgram.id", undefined)
  );
  if (_.isEmpty(curriculumProgramId)) {
    // try extracting curriculumProgramId from admin portal routes
    curriculumProgramId = getCurriculumProgramIdFromRoute(state);
  }

  let academicYearId = _.get(args, "academicYearId", null);

  if (!academicYearId && curriculumProgramId) {
    // If academicYearId is not provided, try to get it from the curriculum program
    academicYearId = getAcademicYearForCurriculumProgram(state, {
      curriculumProgramId,
    })?.id;
  }

  if (curriculumProgramId && academicYearId) {
    const configurationEntityGroups = [
      { entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId },
    ];

    if (isAYWisePermsSetupEnabled) {
      configurationEntityGroups.push({
        entityType: "ACADEMIC_YEAR",
        entityId: academicYearId,
      });
    } else {
      if (userType !== "staff" && isModuleSettingsV2Enabled) {
        configurationEntityGroups.push({
          entityType: "GRADE",
          entityId: gradeId,
        });
      }

      configurationEntityGroups.push({ entityType: "USER", entityId: userId });
    }

    const configurationVariables = {
      configurationEntityGroupFilters: [
        {
          configurationEntityGroups,
        },
      ],
    };

    const settingValue = readConfigurationFragmentV2(
      configurationVariables,
      LEARNING_COURSE_PERM
    );

    isEnabled = parseConfigurationValue(settingValue);
  }

  return isEnabled;
};

export const getAcademicSetupTypeIfLearningCoursesEnabled = args => {
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled(args);
  if (isLearningCoursesEnabled) {
    return getAcademicSetupType(args);
  }
  return null;
};

export const getIsCourseBasedGradingEnabled = args => {
  const academicSetupType = getAcademicSetupType(args);
  return _.isEqual(COURSE_BASED_GRADING, academicSetupType);
};

export const getIsSubjectBasedGradingEnabled = args => {
  const academicSetupType = getAcademicSetupType(args);
  return _.isEqual(SUBJECT_BASED_GRADING, academicSetupType);
};

/**
 * Checks if learning courses are enabled for any combination of curriculum program and academic year
 * within the organization. Returns true if `getIsLearningCoursesEnabled` returns true for at least one
 * such combination.
 **/

export const getIsLearningCoursesEnabledForOrganization = () => {
  const state = store?.getState();

  const curriculumPrograms = getOrganizationCurriculumPrograms(state);

  return _.some(curriculumPrograms, curriculumProgram => {
    const academicYears = getAcademicYearsOfCurriculumProgram(state, {
      curriculumProgramId: curriculumProgram.id,
    });
    return _.some(academicYears, academicYear =>
      getIsLearningCoursesEnabled({
        curriculumProgramId: curriculumProgram.id,
        academicYearId: academicYear.id,
      })
    );
  });
};

/**
 * Inner check operating on already-resolved scope. Memoized on the *resolved* ids so that
 * switching the selected curriculum (or any other input that changes the resolved scope)
 * produces a different cache key.
 **/
const _checkLearningCoursesDisabledForResolvedScope = ({
  programIdsToCheck,
  allowedAYIds,
}) => {
  const state = store?.getState();
  const allowedAYIdsSet = allowedAYIds === null ? null : new Set(allowedAYIds);

  return _.some(programIdsToCheck, curriculumProgramId => {
    const academicYears = getAcademicYearsOfCurriculumProgram(state, {
      curriculumProgramId,
    });
    return _.some(
      academicYears,
      academicYear =>
        (allowedAYIdsSet === null || allowedAYIdsSet.has(academicYear.id)) &&
        !getIsLearningCoursesEnabled({
          curriculumProgramId,
          academicYearId: academicYear.id,
        })
    );
  });
};

const _memoizedCheckLearningCoursesDisabledForResolvedScope = _.memoize(
  _checkLearningCoursesDisabledForResolvedScope,
  ({ programIdsToCheck, allowedAYIds }) =>
    `${programIdsToCheck.join("-")}_${
      allowedAYIds === null ? "*" : allowedAYIds.join("-")
    }`
);

/**
 * Checks if learning courses are disabled for at least one combination of curriculum program
 * and academic year. Returns true if any such combination is still on class flow.
 *
 * Curriculum scope:
 * - If `curriculumProgramIds` is a non-empty array, only those curriculums are checked.
 * - Otherwise falls back to the currently selected curriculum.
 * - If there is no selected curriculum, all of the organization's curriculums are checked.
 *
 * Academic year scope:
 * - If `academicYearIds` is a non-empty array, only those AYs are considered for each curriculum.
 * - Otherwise all AYs of each checked curriculum are considered (`allowedAYIds: null`).
 **/
export const getIsLearningCoursesDisabledForAnyProgramAY = ({
  curriculumProgramIds,
  academicYearIds,
} = {}) => {
  const state = store?.getState();

  let programIdsToCheck = curriculumProgramIds;
  if (_.isEmpty(programIdsToCheck)) {
    const currentCurriculumId = getSelectedCurriculumId(state);
    programIdsToCheck = currentCurriculumId
      ? [currentCurriculumId]
      : _.map(getOrganizationCurriculumPrograms(state), "id");
  }

  const allowedAYIds = _.isEmpty(academicYearIds) ? null : academicYearIds;

  return _memoizedCheckLearningCoursesDisabledForResolvedScope({
    programIdsToCheck: _.sortBy(programIdsToCheck),
    allowedAYIds: allowedAYIds === null ? null : _.sortBy(allowedAYIds),
  });
};

/**
 * Checks if learning courses are enabled for any curriculum program of current academic year
 * within the organization. Returns true if `getIsLearningCoursesEnabled` returns true for at least one
 * such combination.
 **/
export const getIsLearningCoursesEnabledForOrgForCurrentAY = () => {
  const state = store?.getState();
  const curriculumPrograms = getOrganizationCurriculumPrograms(state);

  return _.some(curriculumPrograms, curriculumProgram => {
    const academicYear = getAcademicYearForCurriculumProgram(state, {
      curriculumProgramId: curriculumProgram.id,
    });

    return getIsLearningCoursesEnabled({
      curriculumProgramId: curriculumProgram.id,
      academicYearId: academicYear?.id,
    });
  });
};

/**
 * Checks if learning courses are enabled for all curriculum programs of current academic year
 * Returns true if `getIsLearningCoursesEnabled` returns true for all curriculum programs
 **/

export const getIsLearningCoursesEnabledForAllProgramsForCurrentAY = () => {
  const state = store?.getState();
  const curriculumPrograms = getOrganizationCurriculumPrograms(state);

  if (getIsAYWisePermsSetupEnabled()) {
    return _.every(curriculumPrograms, curriculumProgram => {
      const academicYear = getAcademicYearForCurriculumProgram(state, {
        curriculumProgramId: curriculumProgram.id,
      });

      return getIsLearningCoursesEnabled({
        curriculumProgramId: curriculumProgram.id,
        academicYearId: academicYear?.id,
      });
    });
  }

  /**
   * If AYWisePermsSetup is disabled, learning courses configuration is fetched
   * only for the current curriculum program on the homepage. However, in this case,
   * learning courses are either enabled for all curriculum programs or disabled for all,
   * so checking for the current program is sufficient.
   */
  return getIsLearningCoursesEnabledForOrgForCurrentAY();
};

// LEARNING_COURSE_TYPE_ENUM's value is used in the backend
export const COURSE_TYPE = {
  BLUEPRINT: "BLUEPRINT_COURSE",
  TEACHER: "TEACHER_COURSE",
};

export const getIsBlueprintCourseRevampEnabled = () => {
  return (
    ACLStore.can(BLUEPRINT_COURSE_REVAMP) &&
    getIsAccountConfigurationEnabled() &&
    getIsLearningCoursesEnabled()
  );
};

const getIsCourseCreationRevampEnabled = activeTab => {
  if (activeTab === COURSE_TYPE.BLUEPRINT) {
    return getIsBlueprintCourseRevampEnabled();
  }

  if (activeTab === COURSE_TYPE.TEACHER) {
    return getIsLearningCoursesEnabled() && getIsAccountConfigurationEnabled();
  }

  return false;
};

export const getCoursesCountQueryVariables = ({
  curriculumProgramId,
  curriculumNodeInterfaceType,
}) => {
  return {
    curriculumProgramId,
    curriculumType: curriculumNodeInterfaceType,
  };
};

export const getFeedQueryVariables = ({
  curriculumProgramId,
  curriculumNodeInterfaceType,
  collaboratorFilters,
  learningCoursesState,
  academicYearId,
}) => {
  const searchText = _.get(learningCoursesState, "filters.searchText", "");

  const arrayFilters = {
    grades: _.get(learningCoursesState, "filters.grades", []),
    status: _.get(learningCoursesState, "filters.status", []),
    terms: _.get(learningCoursesState, "filters.terms", []),
    subjects: _.get(learningCoursesState, "filters.subjects", []),
    createdBy: _.get(learningCoursesState, "filters.createdBy", []),
  };

  const {
    grades,
    terms,
    subjects,
    createdBy,
    status,
  } = _.mapValues(arrayFilters, value => (value === "ALL" ? [] : value));

  const courseType = _.get(
    learningCoursesState,
    "activeTab",
    COURSE_TYPE.TEACHER
  );
  const nameFilterConfig = _.first(
    _.get(learningCoursesState, "filterConfig", {})
  );
  const orderByDirection = _.get(nameFilterConfig, "direction", "ASC");

  const orderBy = _.isEmpty(nameFilterConfig) ? "CREATED_AT" : "TITLE";

  return {
    curriculumProgramId,
    curriculumType: curriculumNodeInterfaceType,
    orderByDirection,
    orderBy,
    collaboratorFilters,
    classesFilters: {
      status: "ACTIVE",
    },
    gradeFilters: {
      ...(academicYearId ? { academicYearIds: [academicYearId] } : {}),
    },
    filters: {
      searchText,
      grades,
      academicTerms: terms,
      subjects,
      states: status,
      createdBy,
      types: courseType,
      isCustomOrderingEnabled: true,
      ...(academicYearId ? { academicYears: [academicYearId] } : {}),
    },
  };
};

export const getMemoizedFeed = getMemoizedFunction(data => {
  return _.map(_.get(data, "edges", []), "node");
});

const _getShowDepartmentsColumn = args => {
  return (
    (getIsAccountDepartmentEnabled() ||
      ACLStore.can(ENABLE_DEPARTMENT_AND_LEVEL)) &&
    !getIsSubjectBasedGradingEnabled(args)
  );
};

export const getTableHeaders = ({
  t,
  columnsData,
  isSkeleton,
  hasManageAccess,
  activeTab,
  curriculumProgramId,
}) => {
  const selectedColumns = _.map(
    _.filter(columnsData, column => column.isSelected),
    "id"
  );

  const isCourseCreationRevampEnabled = getIsCourseCreationRevampEnabled(
    activeTab
  );

  const nameObj = {
    key: "NAME",
    value: (
      <div className="flex-1">
        {t("common:label_name_lowercase", { label: t("course:course") })}
      </div>
    ),
    style: !isSkeleton ? { width: "234px", paddingRight: 0 } : {},
    isSticky: true,
    entity: "NAME",
    sortType: "toggle",
  };

  const configurableHeaders = [
    // @yuvraj-BLUEPRINT : uncomment this when blueprint course is enabled
    // {
    //   key: "ASSOCIATED_BLUEPRINT",
    //   value: t("course:blueprint_course"),
    //   style: { width: "188px", paddingRight: 0 },
    // },
    ...(isCourseCreationRevampEnabled
      ? [
          {
            key: "COURSE_CODE",
            value: t("course:course_code"),
            style: { minWidth: "120px", paddingRight: 0 },
          },
          {
            key: "GRADING_PERIOD",
            value: t("common:grading_period_plural"),
            style: { minWidth: "120px" },
          },
          {
            key: "GRADE_LEVEL",
            value: t("common:grade_levels_one_or_many"),
            style: { minWidth: "120px", paddingRight: 0 },
          },
          ...(activeTab === COURSE_TYPE.BLUEPRINT
            ? [
                {
                  key: "ASSOCIATED_COURSES",
                  value: t("course:associated_courses"),
                  style: { minWidth: "128px", paddingRight: 0 },
                },
              ]
            : []),
        ]
      : [
          {
            key: "UID",
            value: t("common:code"),
            style: { minWidth: "120px", paddingRight: 0 },
          },
          {
            key: "DISPLAY_NAME",
            value: t("common:display_name"),
            style: { minWidth: "120px", paddingRight: 0 },
          },
          {
            key: "ASSOCIATED_GRADE",
            value: t("common:grades"),
            style: { minWidth: "120px", paddingRight: 0 },
          },
        ]),
    ...(isCourseCreationRevampEnabled
      ? [
          {
            key: "ASSOCIATED_SUBJECTS",
            value: t("common:subject_one_or_more"),
            style: { minWidth: "128px", paddingRight: 0 },
          },
        ]
      : [
          {
            key: "ASSOCIATED_SUBJECT",
            value: t("common:subject_plural"),
            style: { minWidth: "128px", paddingRight: 0 },
          },
        ]),
    ...(_getShowDepartmentsColumn({ curriculumProgramId })
      ? [
          {
            key: "DEPARTMENT",
            value: t("common:department"),
            style: { minWidth: "128px", paddingRight: 0 },
          },
        ]
      : []),
    {
      key: "ASSOCIATED_CLASS",
      value: t("common:class_plural"),
      style: { minWidth: "128px", paddingRight: 0 },
    },
    ...(isCourseCreationRevampEnabled
      ? [
          {
            key: "STAFF_MEMBERS",
            value: t("schoolSetup:staff_members"),
            style: { minWidth: "128px", paddingRight: 0 },
          },
        ]
      : [
          {
            key: "COLLABORATORS",
            value: t("common:collaborators"),
            style: { minWidth: "128px", paddingRight: 0 },
          },
        ]),
    {
      key: "CREATED_BY",
      value: t("common:created_by"),
      style: { minWidth: "158px", paddingRight: 0 },
    },
    {
      key: "STATUS",
      value: t("common:status"),
      style: { minWidth: "80px", paddingRight: 0 },
    },
  ];

  const actionObj = {
    key: "ACTIONS",
    style: !isSkeleton
      ? {
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }
      : {},
  };

  const sortedHeaders = [
    nameObj,
    ..._.filter(
      _.sortBy(configurableHeaders, header =>
        _.indexOf(selectedColumns, header.key)
      ),
      header => _.includes(selectedColumns, header.key)
    ),
  ];

  if (hasManageAccess) {
    sortedHeaders.push(actionObj);
  }

  return sortedHeaders;
};

export const getDefaultColumnsData = ({
  t,
  activeTab,
  curriculumProgramId,
}) => {
  const isCourseCreationRevampEnabled = getIsCourseCreationRevampEnabled(
    activeTab
  );

  return [
    ...(isCourseCreationRevampEnabled
      ? [
          {
            id: "COURSE_CODE",
            label: t("course:course_code"),
            isSelected: true,
          },
          {
            id: "GRADING_PERIOD",
            label: t("common:grading_period_plural"),
            isSelected: true,
          },
          {
            id: "GRADE_LEVEL",
            label: t("common:grade_levels_one_or_many"),
            isSelected: true,
          },
          ...(activeTab === COURSE_TYPE.BLUEPRINT
            ? [
                {
                  id: "ASSOCIATED_COURSES",
                  label: t("course:associated_courses"),
                  isSelected: true,
                },
              ]
            : []),
        ]
      : [
          {
            id: "UID",
            label: t("common:uid"),
            isSelected: true,
          },
          {
            id: "DISPLAY_NAME",
            label: t("common:display_name"),
            isSelected: false,
          },
          {
            id: "ASSOCIATED_GRADE",
            label: t("common:grade_plural"),
            isSelected: true,
          },
        ]),
    ...(isCourseCreationRevampEnabled
      ? [
          {
            id: "ASSOCIATED_SUBJECTS",
            label: t("common:subject_one_or_more"),
            isSelected: getIsSubjectBasedGradingEnabled(),
          },
        ]
      : [
          {
            id: "ASSOCIATED_SUBJECT",
            label: t("common:subject_plural"),
            isSelected: getIsSubjectBasedGradingEnabled(),
          },
        ]),
    ...(_getShowDepartmentsColumn({ curriculumProgramId })
      ? [
          {
            id: "DEPARTMENT",
            label: t("common:department"),
            isSelected: false,
          },
        ]
      : []),
    {
      id: "ASSOCIATED_CLASS",
      label: t("common:class_plural"),
      isSelected: true,
    },
    ...(isCourseCreationRevampEnabled
      ? [
          {
            id: "STAFF_MEMBERS",
            label: t("schoolSetup:staff_members"),
            isSelected: true,
          },
        ]
      : [
          {
            id: "COLLABORATORS",
            label: t("common:collaborators"),
            isSelected: true,
          },
        ]),
    {
      id: "CREATED_BY",
      label: t("common:created_by"),
      isSelected: false,
    },
    {
      id: "STATUS",
      label: t("common:status"),
      isSelected: true,
    },
  ];
};

export const getActionDropdownMenuItems = ({
  t,
  isClassesTagged,
  isArchived,
}) => {
  const menuItems = [
    {
      key: "EDIT",
      label: t("common:edit_details"),
      icon: <PencilLineOutlined />,
      hide: isArchived,
    },
    {
      key: "DUPLICATE",
      label: t("common:duplicate"),
      icon: <CopyOutlined />,
    },
    {
      key: "ARCHIVE",
      label: t("common:archive"),
      icon: <ArchiveOutlined />,
      hide: isArchived,
    },
    {
      key: "UNARCHIVE",
      label: t("common:restore"),
      icon: <ExportOutlined />,
      hide: !isArchived,
    },
    {
      key: "DELETE",
      label: t("common:delete"),
      icon: <DeleteOutlined />,
      isDestructive: true,
      disabled: isClassesTagged,
      disabledTooltip: t("course:delete_course_disabled_tooltip"),
    },
  ];

  return _.filter(menuItems, item => !item.hide);
};

export const getAvatarData = ({ id, title, profileImageData = {} }) => {
  const { url = "", icon, color: _color, acronym: _acronym } =
    profileImageData || {};

  const color = _color || AVATAR_COLORS[hashString(id) % AVATAR_COLORS.length];

  const name = _acronym ? _.join(_.split(_acronym, ""), " ") : title;

  const AvatarIcon = getSelectedIcon(icon);

  const iconType = !_.isEmpty(_acronym) ? "" : icon ? "icon" : "";

  return { url, color, name, AvatarIcon, iconType };
};

export const getFilterOptions = ({
  t,
  filters,
  gradeOptions,
  subjectOptions,
  termOptions,
  staffOptions,
  statusOptions,
}) => {
  const options = {};
  const defaultSelectedOptions = {};

  const populateDefaultSelectedOptions = ({ filterName, filterOptions }) => {
    const filtersValue = _.get(filters, filterName, []);
    if (filtersValue == "ALL") {
      defaultSelectedOptions[filterName] = _.map(filterOptions, "value");
    } else {
      defaultSelectedOptions[filterName] = filtersValue;
    }
  };

  // Grade
  options["grades"] = {
    options: gradeOptions,
    title: t("common:grade"),
  };
  populateDefaultSelectedOptions({
    filterName: "grades",
    filterOptions: gradeOptions,
  });

  // Term
  options["terms"] = {
    options: termOptions,
    title: t("common:grading_period"),
  };
  populateDefaultSelectedOptions({
    filterName: "terms",
    filterOptions: termOptions,
  });

  // Subject
  options["subjects"] = {
    options: subjectOptions,
    title: t("common:subject"),
  };
  populateDefaultSelectedOptions({
    filterName: "subjects",
    filterOptions: subjectOptions,
  });

  // created by
  options["createdBy"] = {
    options: staffOptions,
    title: t("common:created_by"),
  };
  populateDefaultSelectedOptions({
    filterName: "createdBy",
    filterOptions: staffOptions,
  });

  // status
  options["status"] = {
    options: statusOptions,
    title: t("common:status"),
  };
  populateDefaultSelectedOptions({
    filterName: "status",
    filterOptions: statusOptions,
  });

  return { options, defaultSelectedOptions };
};

export const getStatusOptions = getMemoizedLangFunction(({ t }) => {
  return [
    {
      label: t("common:active"),
      value: "ACTIVE",
    },
    {
      label: t("common:archived"),
      value: "ARCHIVED",
    },
  ];
});

// it's being used in unitLibraryFeed
export const getCoursesOptions = ({ data }) => {
  return _.map(data, ({ id, title, state }) => {
    return {
      value: id,
      label: title,
      isArchived: state === "ARCHIVED",
    };
  });
};

export const getStaffOptionsMemoized = getMemoizedFunction(({ staffData }) => {
  return _.map(staffData, staff => {
    return {
      value: staff.id,
      label: getFormattedUserName({ user: staff }),
    };
  });
});

export const getSubjectOrGradesOptions = ({ data }) => {
  const options = _.map(data, item => {
    const { id, name } = item;
    const assessmentYearTitle = _.get(
      item,
      "ibOfferedSubject.contentSubject.assessmentYearTitle"
    );
    const label = assessmentYearTitle
      ? `${name} (${assessmentYearTitle})`
      : name;

    return {
      value: id,
      label,
    };
  });

  return _.uniqBy(options, "value"); // for dp subjects
};

export const getLevelOptions = ({ levelsData }) => {
  return _.map(levelsData, ({ id, label }) => {
    return {
      value: id,
      label,
    };
  });
};

export const getTermsOptionsMemoized = _.memoize(
  ({ termsData }) => {
    return _.map(termsData, ({ id, label, startDate, endDate }) => {
      return {
        value: id,
        label: `${label} (${getYearDurationFormat({ startDate, endDate })})`,
      };
    });
  },
  ({ termsData }) =>
    JSON.stringify({
      termsData: _.map(termsData, term => ({
        id: term?.id,
        label: term?.label,
        startDate: term?.startDate,
        endDate: term?.endDate,
      })),
    })
);

export const getIsBlueprintCourse = learningCourse => {
  const type = _.get(learningCourse, "type", COURSE_TYPE.TEACHER);
  return _.isEqual(type, COURSE_TYPE.BLUEPRINT);
};

export const getCourseManageModalContent = ({
  createCourseModalData,
  t,
  isBlueprintCourse = false,
}) => {
  const { mode, title = "" } = createCourseModalData || {};
  switch (mode) {
    case "CREATE":
      return {
        headerText: t("common:create_with_label", {
          label: isBlueprintCourse
            ? t("schoolSetup:a_blueprint_course")
            : t("schoolSetup:a_course"),
        }),
        saveBtnText: t("common:create"),
        cancelBtnText: t("common:cancel"),
      };
    case "EDIT":
      return {
        headerText: t("common:edit_with_label", {
          label: isBlueprintCourse
            ? t("schoolSetup:a_blueprint_course")
            : t("schoolSetup:a_course"),
        }),
        saveBtnText: t("common:save_changes"),
        cancelBtnText: t("common:discard_changes"),
      };
    case "DUPLICATE":
      return {
        headerText: t("common:duplicate_with_quoted_label", {
          label: title,
        }),
        saveBtnText: t("common:duplicate"),
        cancelBtnText: t("common:cancel"),
      };
    default:
      return { headerText: "", saveBtnText: "", cancelBtnText: "" };
  }
};

export const getShouldShowCourseDisplayName = () => {
  const isStaffUser = getIsStaff();
  return (
    getIsLearningCoursesEnabled() &&
    !isStaffUser &&
    ACLStore.can(SHOW_COURSE_DISPLAY_NAME)
  );
};

export const getCourseDisplayData = course => {
  const showCourseDisplayName = getShouldShowCourseDisplayName();

  if (showCourseDisplayName) {
    return {
      name:
        _.get(course, "learningCourse[0].academicCourse.label") ??
        _.get(course, "learningCourse[0].title"),
      profileImageData: _.get(course, "learningCourse[0].displayMetadata"),
    };
  }

  return {
    name: course.title,
    profileImageData: course.profileImageData,
  };
};

export const getGlobalGradeUid = () => {
  const state = store.getState();
  let gradeId = _.get(state, "teacher.selected_class.selected_grades[0]", "");

  if (_.isEmpty(gradeId)) {
    const assessmentId = _.get(
      state,
      "assessment.assessmentData.assessmentId",
      ""
    );
    const node = getAssessmentGradesFromCache({
      assessmentId,
    });

    gradeId = _.get(node, "grades[0].id", null);
  }

  const isCourseFlowEnabled = getIsLearningCoursesEnabled();
  const learningCoursePrimaryGradeId = _.get(
    state,
    "learningCourse.selectedLearningCourse.primaryGradeId",
    null
  );

  const resolvedGradeId = isCourseFlowEnabled
    ? learningCoursePrimaryGradeId
    : gradeId;
  let globalGradeUid;
  if (resolvedGradeId) {
    const gradeDetails = getGradeBasicDetailsFromCache(resolvedGradeId);
    globalGradeUid = _.get(gradeDetails, "globalGrade.uid", null);
  }

  if (_.isEmpty(globalGradeUid)) {
    const assessmentId = _.get(
      state,
      "assessment.assessmentData.assessmentId",
      ""
    );
    const node = getAssessmentGradesFromCache({
      assessmentId,
    });
    globalGradeUid = _.get(node, "grades[0].globalGrade.uid", null);
  }

  return globalGradeUid;
};
